import * as XLSX from 'xlsx';

/**
 * Reads an Excel file and returns its content as a JSON object.
 * @param {File} file - The Excel file to read.
 * @returns {Promise<object>} A promise that resolves to the Excel data.
 */
export const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = event.target.result;
                // Read workbook with enhanced options for date handling
                const workbook = XLSX.read(data, { 
                    type: 'binary',
                    // Interpret date-style numbers as JS Date objects
                    cellDates: true,
                    // Populate the cell.z property (the number-format code)
                    cellNF: true,
                    // Populate the cell.w property (the formatted text)
                    cellText: true
                });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                
                // Convert to JSON with date handling
                const jsonData = XLSX.utils.sheet_to_json(sheet, {
                    // Keep raw values for proper date handling
                    raw: false,
                    // Use formatted text when available
                    defval: ''
                });
                
                // Store the raw sheet data for advanced processing if needed
                const result = {
                    data: jsonData,
                    sheet: sheet,
                    workbook: workbook
                };
                
                resolve(result);
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = (error) => {
            reject(error);
        };
        reader.readAsBinaryString(file);
    });
};

/**
 * Extracts the headers from the Excel data.
 * @param {object} result - The Excel result object containing data, sheet, and workbook.
 * @returns {string[]} An array of header strings.
 */
export const getExcelHeaders = (result) => {
    if (!result || !result.data || result.data.length === 0) {
        return [];
    }
    return Object.keys(result.data[0]);
};

/**
 * Extracts the rows from the Excel data.
 * @param {object} result - The Excel result object containing data, sheet, and workbook.
 * @returns {object[]} An array of row objects.
 */
export const getExcelRows = (result) => {
    if (!result || !result.data) {
        return [];
    }
    return result.data;
};

/**
 * Gets detailed cell information including formatting for a specific cell.
 * @param {object} result - The Excel result object containing data, sheet, and workbook.
 * @param {string} cellAddress - The cell address (e.g., 'A1', 'B2').
 * @returns {object} Cell information including value, type, format, and formatted text.
 */
export const getCellInfo = (result, cellAddress) => {
    if (!result || !result.sheet || !result.sheet[cellAddress]) {
        return null;
    }
    
    const cell = result.sheet[cellAddress];
    return {
        value: cell.v,           // Raw value
        type: cell.t,            // Cell type ('n' for number, 's' for string, 'd' for date, etc.)
        format: cell.z,          // Number format string (e.g., "m/d/yyyy")
        formattedText: cell.w,   // Formatted text as string
        isDate: cell.t === 'd' || (cell.t === 'n' && cell.z && isDateFormat(cell.z))
    };
};

/**
 * Checks if a number format string represents a date format.
 * @param {string} format - The Excel number format string.
 * @returns {boolean} True if the format is a date format.
 */
const isDateFormat = (format) => {
    if (!format) return false;
    
    // Common date format patterns
    const datePatterns = [
        /[yY]+/,        // Year
        /[mM]+/,        // Month
        /[dD]+/,        // Day
        /[hH]+/,        // Hour
        /[sS]+/,        // Second
        /AM\/PM/i,      // AM/PM
        /A\/P/i         // A/P
    ];
    
    return datePatterns.some(pattern => pattern.test(format));
};

/**
 * Processes Excel data to properly format dates and other special values.
 * @param {object} result - The Excel result object containing data, sheet, and workbook.
 * @returns {object[]} Processed array of row objects with properly formatted values.
 */
export const getProcessedExcelRows = (result) => {
    if (!result || !result.data || !result.sheet) {
        return [];
    }
    
    const processedRows = result.data.map((row, rowIndex) => {
        const processedRow = { ...row };
        
        // Process each cell in the row
        Object.keys(row).forEach(columnName => {
            // Find the cell address for this row and column
            const columnIndex = Object.keys(result.data[0]).indexOf(columnName);
            const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: columnIndex }); // +1 because first row is headers
            
            const cellInfo = getCellInfo(result, cellAddress);
            if (cellInfo && cellInfo.isDate) {
                // Use the formatted text for dates, or format the date value
                if (cellInfo.formattedText) {
                    processedRow[columnName] = cellInfo.formattedText;
                } else if (cellInfo.value instanceof Date) {
                    processedRow[columnName] = cellInfo.value.toLocaleDateString();
                } else if (typeof cellInfo.value === 'number') {
                    // Convert Excel serial date to JavaScript Date
                    const excelEpoch = new Date(1900, 0, 1);
                    const jsDate = new Date(excelEpoch.getTime() + (cellInfo.value - 2) * 24 * 60 * 60 * 1000);
                    processedRow[columnName] = jsDate.toLocaleDateString();
                }
            }
        });
        
        return processedRow;
    });
    
    return processedRows;
};
